// Global
import React, { useCallback, useRef } from 'react';
import { tv } from 'tailwind-variants';

// Local
import { Layout } from '.generated/Feature.CW.model';
import { withStandardComponentWrapper } from 'helpers/HOC';
import { SvgIcon } from 'helpers/SvgIcon';
import { useScrollElementIntoView } from 'lib/hooks/useScrollElementIntoView';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { useWatchSideNavActiveState } from 'helpers/Context/SideNavContext';
import { useAccordionItemContext } from 'helpers/Context/AccordionContext';
import { PlaceholderWrapper } from 'helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import classNames from 'classnames';
import ComponentWrapper from 'helpers/SitecoreWrappers/ComponentWrapper/ComponentWrapper';
import { useCurrentScreenType } from 'lib/utils/get-screen-type';
import useIsEditing from 'lib/hooks/useIsEditing';
import { EditingHelpText } from 'helpers/Editing/EditingHelpText';
import { ComponentRendering, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { useAppointmentModal } from 'helpers/Context/AppointmentModalProvider';

export type SideNavSectionProps = Layout.SideNavSection_Component & {
  renderMobileAsAccordion?: boolean;
  variantName?: string;
  isParentChildSideNav?: boolean;
  firstSectionUid?: string;
} & {
  params: {
    Theme?: string;
    Underline?: string; // 'nounderlines' | 'underlineforboth' | 'underlineonlyundercomponents' | 'underlineonlyunderheader'
  };
};

type LayoutData = {
  componentName?: string;
  url?: string;
  dataSource?: string;
  placeholders?: {
    [key: string]: LayoutData[];
  };
  fields?: Record<string, unknown>;
  params?: Record<string, unknown>;
};

function hasComponent(layoutData: LayoutData | LayoutData[], componentName: string): boolean {
  if (Array.isArray(layoutData)) {
    return layoutData.some((item) => hasComponent(item, componentName));
  }
  if (typeof layoutData === 'object' && layoutData !== null) {
    if (layoutData.componentName === componentName) {
      return true;
    }
    if (layoutData.placeholders) {
      return Object.values(layoutData.placeholders).some((placeholder) =>
        hasComponent(placeholder, componentName)
      );
    }
  }
  return false;
}

const SideNavSection = (props: SideNavSectionProps): JSX.Element => {
  const { renderMobileAsAccordion, variantName, isParentChildSideNav = false } = props;
  const { heading } = props?.fields || {};
  const { uid } = props?.rendering || {};
  const { sitecoreContext } = useSitecoreContext();

  const underlineType = props?.params?.Underline?.toLowerCase() || 'underlineonlyunderheader';

  const { currentScreenWidth } = useCurrentScreenType();
  const { itemName } = useAppointmentModal();

  const phKey = `hztl-section-content-${props?.params?.DynamicPlaceholderId}`;

  const accordionId = `accordion-${uid}`;

  const isComponentFound = hasComponent(
    sitecoreContext?.route as LayoutData,
    'OpenSchedulingWizard'
  );

  /*
   * REFS
   */

  const accordionItemHeadlineRef = useRef<HTMLDivElement>(null);

  const accordionItemBodyRef = useRef<HTMLDivElement>(null);

  /*
   * State
   */

  const { isOpening, toggleOpen, isVisible, scrollToOpenPanel } = useAccordionItemContext(
    uid ?? '',
    accordionItemBodyRef
  );

  /*
   * Convenience Methods
   */

  const toggleAccordion = useCallback(() => {
    toggleOpen();
  }, [toggleOpen]);

  /*
   * Lifecycle
   */

  useScrollElementIntoView(accordionItemHeadlineRef.current, {
    stickyHeaderId: 'header',
    condition: scrollToOpenPanel && isOpening,
    animationElement: accordionItemBodyRef.current,
    scrollTargetId: accordionId,
  });

  // Update the active
  useWatchSideNavActiveState(accordionItemHeadlineRef, uid);

  const isEditing = useIsEditing();
  const { setOfficeHoursSectionId } = useAppointmentModal();

  React.useEffect(() => {
    const component = props?.rendering?.placeholders?.[phKey]?.find(
      (component: ComponentRendering) => component.componentName === 'OfficeHours'
    );
    if (component && setOfficeHoursSectionId) {
      setOfficeHoursSectionId({
        uid: uid ?? '',
        toggleOpen,
      });
    }
  }, [phKey, props?.rendering?.placeholders, setOfficeHoursSectionId, toggleOpen, uid]);

  // location listing open accordion if user is coming from show more location link from provider listing
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);
    const isShowMoreLocation = urlParams.get('showMoreLocation') === 'true';

    if (isShowMoreLocation && currentScreenWidth < 768 && props?.rendering?.placeholders?.[phKey]) {
      const hasLocationListing = props?.rendering?.placeholders?.[phKey]?.some(
        (component: ComponentRendering) => component.componentName === 'LocationListing'
      );

      if (hasLocationListing && toggleOpen) {
        toggleOpen();
      }
    }
  }, [phKey, props?.rendering?.placeholders, toggleOpen, currentScreenWidth]);

  /*
   * Rendering
   */

  const { base, buttonWrapper, headline, iconWrapper, contentContainer } = TAILWIND_VARIANTS({
    isOpening,
    isVisible,
    mobileAccordion: renderMobileAsAccordion,
  });

  const hasProviderCardListing = props?.rendering?.placeholders?.[phKey]?.some(
    (component: ComponentRendering) => component.componentName === 'ProviderCardListing'
  );

  if (
    props?.rendering?.placeholders?.[phKey]?.length === 0 &&
    props?.fields?.subHeading?.value === '' &&
    !isEditing
  ) {
    return <></>;
  }

  return (
    <ComponentWrapper
      params={props?.params}
      className={classNames(
        props?.params?.Theme ? 'bg-theme-bg' : '',
        variantName == 'staticfulllayout' ? 'py-24 lg:py-[80px]' : '!py-0'
      )}
    >
      <div
        className={classNames(
          base(),
          (variantName == 'staticfulllayout' || variantName == 'staticsidebarlayout') &&
            'px-10 lg:px-20',
          variantName == 'staticfulllayout'
            ? 'w-full'
            : variantName === 'staticfulllayout' || variantName === 'staticsidebarlayout'
              ? `max-w-[898px] mx-auto ${heading?.value === '' ? 'pt-24' : ''}`
              : variantName === 'parentchildlayout'
                ? `${!isParentChildSideNav && !isEditing ? `max-w-full px-10 md:px-0 ${heading?.value === '' ? 'pt-24' : ''}` : `max-w-[738px] px-10 ${heading?.value === '' ? 'pt-24' : ''}`}`
                : `max-w-[738px] px-10 ${heading?.value === '' ? 'pt-24' : ''}`,
          variantName === 'default' && !isOpening
            ? 'pb-0'
            : variantName !== 'staticfulllayout'
              ? 'pb-24'
              : '',
          variantName == 'staticfulllayout' ? 'max-w-screen-xxl mx-auto' : 'md:pb-24',

          currentScreenWidth > 0 && currentScreenWidth < 768 && variantName === 'default'
            ? 'relative before:absolute before:left-1/2 before:-bottom-1 before:border-b-4 before:rounded-xl before:border-gray-50 before:w-[calc(100%-40px)] before:-translate-x-1/2'
            : ''
        )}
        data-component="authorable/shared/layout/side-nav-mobile-accordion-item"
        key={accordionId}
      >
        <EditingHelpText>
          <h3 className="font-centuryGothic p-3">
            Section ID :- <b>{accordionId}</b>
          </h3>
        </EditingHelpText>
        <div ref={accordionItemHeadlineRef}>
          {/* Shown when viewed as accordion (Mobile only) */}
          <button
            aria-expanded={isOpening}
            className={classNames(buttonWrapper(), 'py-16')}
            onClick={toggleAccordion}
            type="button"
          >
            <PlainTextWrapper field={heading} tag="h2" className="z-20" />
            <span className={iconWrapper()}>
              <SvgIcon
                icon={!isOpening ? 'plus' : 'minus'}
                viewBox={!isOpening ? '0 0 24 24' : '0 0 21 5'}
                className={classNames(
                  'text-blue-base transition-all duration-300 !w-[28px] !h-[28px]',
                  !isOpening ? 'rotate-90' : 'rotate-0'
                )}
              />
            </span>
          </button>
          {/* Shown when not an accordion */}
          {(heading?.value !== '' || isEditing) && (
            <h2
              className={classNames(
                headline(),
                heading?.value !== '' &&
                  (underlineType === 'nounderlines' ||
                    underlineType === 'underlineonlyundercomponents') &&
                  'pb-8',
                heading?.value !== '' && variantName !== 'staticfulllayout' && 'pt-24'
              )}
            >
              <PlainTextWrapper field={heading} />{' '}
              {isComponentFound && props?.firstSectionUid === uid && (
                <PlainTextWrapper field={{ value: itemName }} />
              )}
            </h2>
          )}

          {(heading?.value !== '' || isEditing) &&
            (underlineType === 'underlineforboth' ||
              underlineType === 'underlineonlyunderheader') && (
              <span
                className={classNames(
                  'border-b-4 rounded-xl border-theme-row-spliter-border-color mb-16',
                  variantName === 'default' ? 'hidden md:block ' : 'block'
                )}
              ></span>
            )}
        </div>

        <div
          aria-labelledby={accordionId}
          className={contentContainer()}
          role="region"
          ref={accordionItemBodyRef}
        >
          <div
            className={classNames(
              'sidenav-section-content',
              variantName === 'default' && 'pt-8 md:pt-0'
            )}
          >
            {(props?.fields?.subHeading?.value !== '' || isEditing) && (
              <p className="mb-16 text-theme-subtext sidenav-section-subheading">
                <PlainTextWrapper field={props?.fields?.subHeading} />
              </p>
            )}

            <div
              className={classNames(
                'parent sidenav-section-component [&>div]:p-0 [&_.component>*]:p-0 [&_.component>*]:m-0',
                hasProviderCardListing ? 'provider-listing-section' : '',
                underlineType === 'nounderlines' || underlineType === 'underlineonlyunderheader'
                  ? variantName === 'staticfulllayout'
                    ? 'space-y-24 md:space-y-[56px]'
                    : 'space-y-16 md:space-y-[40px]'
                  : 'divide-y divide-theme-row-spliter-border-color [&>*]:!py-24 md:[&>*]:!py-[56px]',
                isEditing &&
                  (underlineType === 'nounderlines' || underlineType === 'underlineonlyunderheader'
                    ? variantName === 'staticfulllayout'
                      ? '[&>div]:space-y-24 [&>div]:md:space-y-[56px]'
                      : '[&>div]:space-y-16 [&>div]:md:space-y-[40px]'
                    : 'divide-y divide-theme-row-spliter-border-color [&>div>*]:!py-24 md:[&>div>*]:!py-[56px] [&_.ee-help-text-block]:!py-0 [&>div]:divide-y [&>div]:divide-theme-row-spliter-border-color'),
                '[&>*:first-child]:!pt-0 [&>*:last-child]:!pb-0'
              )}
              data-accordionItemId={accordionId}
            >
              <PlaceholderWrapper name={phKey} rendering={props.rendering} isSidenavPart={true} />
            </div>
          </div>
        </div>
      </div>
    </ComponentWrapper>
  );
};

export const Default = withStandardComponentWrapper(SideNavSection);

const TAILWIND_VARIANTS = tv({
  defaultVariants: {
    isOpen: false,
    isVisible: true,
  },
  slots: {
    base: ['relative'],
    headline: [
      'items-center',
      'justify-between',
      'font-bold',
      'font-centuryGothic',
      'text-[30px]',
      'md:text-36',
      'leading-43',
      'mb-8',
      'transition-none',
      'w-full',
      'text-theme-subtext',
    ],
    buttonWrapper: [
      'cursor-pointer',
      'duration-300',
      'items-center',
      'justify-between',
      'gap-5',
      'md:p-3',
      'text-[30px]',
      'text-left',
      'text-theme-subtext',
      'font-bold',
      'font-centuryGothic',
      'leading-[36px]',
      'transition-none',
      'w-full',
      'font-bold',
      'relative',
      'transition-all',
      'focus:outline-theme-brand3',
      'before:absolute before:h-full before:-translate-x-1/2 before:w-screen before:z-[10] z-[0] before:left-1/2',
      'active:before:bg-blue-50',
    ],
    contentContainer: [
      'duration-300',
      'ease-in-out',
      'md:grid',
      'transition-all',
      'grid-rows-[1fr]',
      'opacity-100',
    ],
    iconWrapper: ['transform', 'transition-transform', 'md:hidden', 'z-20'],
    richTextWrapper: ['font-normal', 'mb-0', 'p-4', 'text-lg'],
  },
  variants: {
    isOpening: {
      false: {
        contentContainer: ['grid-rows-[0fr]', 'opacity-0'],
        iconWrapper: [],
      },
      true: {
        iconWrapper: ['rotate-180'],
        contentContainer: [
          'grid-rows-[1fr]',
          'md:grid-rows-[1fr]',
          'opacity-100',
          'md:opacity-100',
        ],
      },
    },

    // We use this for compound variants, but need placeholder here to keep TypeScript happy.
    isVisible: {
      false: {
        contentContainer: ['md:grid', 'hidden'],
      },
      true: {},
    },

    mobileAccordion: {
      false: {
        headline: ['flex'],
        buttonWrapper: ['hidden'],
      },
      true: {
        headline: ['hidden', 'md:flex'],
        buttonWrapper: ['flex', 'md:hidden'],
      },
    },
  },
  compoundVariants: [
    // Visible false, for all the accordion variants
    {
      isVisible: false,
      mobileAccordion: true,
      class: {
        contentContainer: ['md:grid', 'hidden'],
      },
    },
    {
      isVisible: false,
      mobileAccordion: false,
      class: {
        contentContainer: ['md:grid', 'grid'],
      },
    },

    // isOpening false, for all the accordion variants
    {
      isOpening: false,
      mobileAccordion: true,
      class: {
        contentContainer: ['grid-rows-[0fr]', 'md:grid-rows-[1fr]', 'opacity-0', 'md:opacity-100'],
      },
    },
    {
      isOpening: false,
      mobileAccordion: false,
      class: {
        contentContainer: [
          'grid-rows-[1fr]',
          'md:grid-rows-[1fr]',
          'opacity-100',
          'md:opacity-100',
        ],
      },
    },
  ],
});
